import classnames from 'classnames';
import { isEqual } from 'date-fns/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import '@eika/calendar-style';

import CalendarView from './elements/CalendarView';
import { CalendarConfig, Display, Language, ListConfig, Notification } from './types';
import {
    convertToStandardKeyboardCode,
    generateId,
    getCalendarConfig,
    getNextFocusDateInTableByKeyboardArrow,
    getPrimaryDateInView,
    isCalendarInFocus,
    isTimeZoneDifferent,
    setDomTabFocus,
} from './utils';

export interface CalendarProps {
    selected: ZonedDate | undefined;
    onSelect: (v?: ZonedDate) => void;
    className?: string;
    id?: string;
    show?: boolean;
    language?: Language;
    display?: Display;
    view?: ZonedDate;
    minDate?: ZonedDate;
    maxDate?: ZonedDate;
    onClose?: () => void;
    notification?: Notification;
    popup?: boolean;
    alignPopupRight?: boolean;
    listConfig?: ListConfig;
    setSpanDates?: Dispatch<SetStateAction<ZonedDate[]>>;
    bankHolidayDates?: ZonedDate[];
    alwaysShowYear?: boolean;
}

const Calendar = ({
    selected,
    show = true,
    minDate,
    maxDate,
    display,
    language,
    popup,
    alignPopupRight = false,
    onClose,
    onSelect,
    listConfig,
    notification,
    setSpanDates,
    bankHolidayDates,
    alwaysShowYear = false,
    ...props
}: CalendarProps): JSX.Element | null => {
    const [timeZoneDifferent] = useState<boolean>(isTimeZoneDifferent());
    const [id] = useState<string>(props.id ?? generateId('sgw-calendar'));

    const focusRef = useRef<HTMLButtonElement>(null);

    const [view, setView] = useState<ZonedDate>(props.view ?? selected ?? getZonedDate());
    const [focus, setFocus] = useState<ZonedDate | undefined>(undefined);
    const [spanDateState, setSpanDateState] = useState<ZonedDate[] | undefined>(undefined);
    const [copyOfShow, setCopyOfShow] = useState<boolean>(show);
    const [previouslyFocusedElement, setPreviouslyFocusedElement] = useState<HTMLElement | null>(null);

    const wrapper = useRef<HTMLDivElement>(null);

    const primaryDateInView = useMemo<ZonedDate>(() => getPrimaryDateInView(view, selected), [selected, view]);

    const calendar = useMemo<CalendarConfig>(
        () =>
            getCalendarConfig(
                view ?? selected,
                focusRef,
                () => {
                    setFocus(primaryDateInView);
                },
                selected,
                minDate,
                maxDate,
                focus,
                primaryDateInView,
                language,
                alwaysShowYear,
            ),
        [selected, view, focus, setFocus, primaryDateInView, minDate, maxDate, language, focusRef, alwaysShowYear],
    );

    useEffect(() => {
        if (spanDateState && calendar.dateSpan?.length && setSpanDates) {
            const oldStart = getZonedDate(spanDateState[0]);
            const oldEnd = getZonedDate(spanDateState[1]);
            const newStart = getZonedDate(calendar.dateSpan[0]);
            const newEnd = getZonedDate(calendar.dateSpan[1]);
            if (!(isEqual(oldStart, newStart) && isEqual(oldEnd, newEnd))) {
                setSpanDates(calendar.dateSpan);
                setSpanDateState(calendar.dateSpan);
            }
        } else if (!spanDateState && calendar.dateSpan && setSpanDates) {
            setSpanDates(calendar.dateSpan);
            setSpanDateState(calendar.dateSpan);
        }
    }, [calendar.dateSpan, setSpanDateState, spanDateState, setSpanDates]);

    useEffect(() => {
        if (show !== copyOfShow) {
            if (show !== false && popup) {
                const t = document.activeElement;
                if (t instanceof HTMLElement) {
                    setPreviouslyFocusedElement(t);
                }
                setFocus(primaryDateInView);
                if (focusRef.current) {
                    focusRef.current.focus();
                }
            } else {
                setFocus(undefined);
                if (previouslyFocusedElement) {
                    previouslyFocusedElement.focus();
                }
            }
        }
        setCopyOfShow(show);
    }, [
        show,
        popup,
        copyOfShow,
        setCopyOfShow,
        previouslyFocusedElement,
        setPreviouslyFocusedElement,
        focus,
        setFocus,
        primaryDateInView,
    ]);

    useEffect(() => {
        if (show && focusRef.current) {
            // The date in focus would not be announced by VoiceOver unless
            // the focus was delayed slightly, when using the arrow keys to
            // navigate to a new month
            const dateButton = focusRef.current;
            setTimeout(() => {
                dateButton.focus();
            }, 0);
        }
    });

    const closeCalendar = useCallback((): void => {
        if (typeof onClose === 'function') {
            onClose();
        }
        setView(selected ?? props.view ?? getZonedDate());
    }, [setView, selected, props.view, onClose]);

    const updateCalendar = (zonedDate: ZonedDate): void => {
        setView(zonedDate);
    };

    const selectDate = useCallback(
        (zonedDate: ZonedDate): void => {
            setFocus(zonedDate);
            setView(zonedDate);
            onSelect(zonedDate);
        },
        [setFocus, setView, onSelect],
    );

    const handleKeydown = useCallback(
        (e: KeyboardEvent): void => {
            if (show === false) {
                return;
            }

            const key = convertToStandardKeyboardCode(e.key);
            if (!key) {
                return;
            }

            e.stopPropagation();
            /* Avgjort at tab navigering skal holde innen for calendar */
            if (key === 'Tab') {
                if (popup) {
                    e.preventDefault();
                    setDomTabFocus(
                        wrapper,
                        () => {
                            setFocus(undefined);
                        },
                        e.shiftKey,
                    );
                } else if (focus) {
                    setFocus(undefined);
                }
            } else if (key === 'Escape') {
                if (popup) {
                    closeCalendar();
                }
            } else if (key === 'Enter' || key === ' ') {
                if (focus) {
                    e.preventDefault();
                    selectDate(focus);
                }
            } else if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown') {
                if (!popup && !isCalendarInFocus(wrapper)) {
                    return;
                }

                e.preventDefault();
                const note = getNextFocusDateInTableByKeyboardArrow(calendar.list, view, key, focus, minDate, maxDate);

                setFocus(note.nextFocus);

                if (!note.stayInCurrentView) {
                    setView(note.nextFocus);
                }
            }
        },
        [
            view,
            setView,
            focus,
            setFocus,
            calendar.list,
            wrapper,
            minDate,
            maxDate,
            popup,
            closeCalendar,
            selectDate,
            show,
        ],
    );

    useEffect(() => {
        const { current } = wrapper;
        if ((!popup || show !== false) && current) {
            current.addEventListener('keydown', handleKeydown);
        }
        return () => current?.removeEventListener('keydown', handleKeydown);
    }, [handleKeydown, popup, show, wrapper]);

    useEffect(() => {
        setView(props.view ?? selected ?? getZonedDate());
    }, [props.view, selected]);

    return show === false || view.getTime() !== calendar.stamp ? null : (
        <div
            className={classnames('sgw-calendar__wrapper', {
                'align-popup-right': alignPopupRight && !!popup,
                show: show && !!popup,
            })}
        >
            {popup && (
                <div className="sgw-calendar__popup__overlay" onClick={closeCalendar} onKeyDown={closeCalendar} />
            )}
            <div
                id={id}
                role="application"
                aria-label="Kalender"
                ref={wrapper}
                className={classnames('sgw-calendar', {
                    'sgw-calendar__popup': !!popup,
                    'align-popup-right': alignPopupRight && !!popup,
                    show: show && !!popup,
                })}
            >
                <CalendarView
                    calendar={calendar}
                    onNavigate={updateCalendar}
                    onSelect={selectDate}
                    displayType={display ? `-display-${display}` : undefined}
                    notification={notification}
                    selected={selected}
                    timeZoneDifferent={timeZoneDifferent}
                    language={language}
                    listConfig={listConfig}
                    bankHolidayDates={bankHolidayDates}
                />
            </div>
        </div>
    );
};

export default Calendar;
